import { Reducer, Action } from "redux";
import { OrderPadState, KnownOrderPadAction, unloadedOrderPadState, OrderPadLine, resetOrderPadState } from ".";
import { v1 as uuidv1 } from 'uuid';
import { encryptAndStoreValueToLocalStorage, getDecryptedValueFromLocalStorage } from "../../lib/methods/localStorageBasedMethods";
import { getNextRealitexOrderLineNo, getNextRealitexUnallocatableOrderLineNo } from "../../lib/hooks/useFetchAndSetNextRealitexOrderNo";

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducer: Reducer<OrderPadState> = (state: OrderPadState | undefined, incomingAction: Action): OrderPadState => {
  if (state === undefined) 
    return unloadedOrderPadState;
  
  const loggedInContactId = getDecryptedValueFromLocalStorage('ecommContactIdEnc');
  const action = incomingAction as KnownOrderPadAction;

  switch (action.type) {
   
    case "ADD_LINE":
      const currentlyConstructingOrderNo = getDecryptedValueFromLocalStorage(`order-pad-order-no:${loggedInContactId}Enc`); //todo - might need to get from central state at this point or call and get it if can't access it
      //the new web API allocation routine needs an orderno and line no generating, so add this using this method
      const nextRealitexOrderLineNumber = getNextRealitexOrderLineNo();

      //add the props required
      var newOrderPadLine: OrderPadLine = {
        ...action.line,
        lineId: uuidv1(),
        realitexOrderLineNumber: nextRealitexOrderLineNumber,
        realitexOrderLineStatus: '',
        realitexOrderNumber: currentlyConstructingOrderNo ? currentlyConstructingOrderNo : '0',
        aliasRef: state.aliasRefToAdd && state.aliasRefToAdd !== '' ? state.aliasRefToAdd : action.line.aliasRef ? action.line.aliasRef : '', //for stockRefToAdd, this is handled elsewhere, likely in a useeffect somewhere, but couldn't find it so handled here
        extendedDescription: state.extendedDescriptionToAdd && state.extendedDescriptionToAdd !== '' ? state.extendedDescriptionToAdd : action.line.extendedDescription ? action.line.extendedDescription : ''
      }

      //the API will reissue the deallocateAt date when a new line is added to the order-pad, and all lines on the order-pad are handled with this single datetime - all are removed if the user doesn't click to continue shopping - so change the deallocateAt date (against each line AND against each allocatedStockPiece on each line) to that of the newly added piece
      let linesToUseInStateAfterPotentialProcessing = null;
      if(action.useWebApiForStockPieceAllocation){
         linesToUseInStateAfterPotentialProcessing = state.lines.map((line, index) => {
          line.deallocateAt = action.line.deallocateAt;
          return line;
        })
      }
      else{
        linesToUseInStateAfterPotentialProcessing = state.lines
      }
     
      var result = encryptAndStoreValueToLocalStorage(`order-pad-lines:${loggedInContactId}Enc`, JSON.stringify([...linesToUseInStateAfterPotentialProcessing, newOrderPadLine]));
      return {
        ...state,
          extendedDescriptionToAdd: undefined,
          aliasRefToAdd: undefined,
          lines: [...linesToUseInStateAfterPotentialProcessing, newOrderPadLine],
          stockRefToAdd: undefined
      };


      case "ADD_UNPAYABLE_LINE":

        //the new web API allocation routine needs an orderno and line no generating, so add this using this method
        const currentlyconstructingNonPayableOrderNo = getDecryptedValueFromLocalStorage(`order-pad-unpayable-order-no:${loggedInContactId}Enc`)
        const nextUnallocatbleLinesRealitexOrderLineNumber = getNextRealitexUnallocatableOrderLineNo();
  
        //add the props required
        var newUnallocatableOrderPadLine: OrderPadLine = {
          ...action.unpayableLineToAdd,
          lineId: uuidv1(),
          //realitexOrderLineNumber: 0,
          realitexOrderLineNumber: nextUnallocatbleLinesRealitexOrderLineNumber,
          realitexOrderLineStatus: '',
          realitexOrderNumber: currentlyconstructingNonPayableOrderNo ? currentlyconstructingNonPayableOrderNo : '0',
          aliasRef: state.aliasRefToAdd && state.aliasRefToAdd !== '' ? state.aliasRefToAdd : action.unpayableLineToAdd && action.unpayableLineToAdd.aliasRef ? action.unpayableLineToAdd.aliasRef : '', //for stockRefToAdd, this is handled elsewhere, likely in a useeffect somewhere, but couldn't find it so handled here
          extendedDescription: state.extendedDescriptionToAdd && state.extendedDescriptionToAdd !== '' ? state.extendedDescriptionToAdd : action.unpayableLineToAdd && action.unpayableLineToAdd.extendedDescription ? action.unpayableLineToAdd.extendedDescription : ''
        }
  
  
        let unallocatableLinesToUseInStateAfterPotentialProcessing : OrderPadLine[];
        if(action.useWebApiForStockPieceAllocation){//the API will reissue the deallocateAt date when a new line is added to the order-pad, and all lines on the order-pad are handled with this single datetime - all are removed if the user doesn't click to continue shopping - so change the deallocateAt date (against each line AND against each allocatedStockPiece on each line) to that of the newly added piece
          unallocatableLinesToUseInStateAfterPotentialProcessing = state.unpayableOrderLines.map((line, index) => {
            line.deallocateAt = action.unpayableLineToAdd.deallocateAt;
            return line;
          })
        }
        else{
          unallocatableLinesToUseInStateAfterPotentialProcessing = state.unpayableOrderLines
        }
       
        var result = encryptAndStoreValueToLocalStorage(`order-pad-unpayable-lines:${loggedInContactId}Enc`, JSON.stringify([...unallocatableLinesToUseInStateAfterPotentialProcessing, newUnallocatableOrderPadLine]));
        return {
          ...state,
            extendedDescriptionToAdd: undefined,
            aliasRefToAdd: undefined,
            unpayableOrderLines: [...unallocatableLinesToUseInStateAfterPotentialProcessing, newUnallocatableOrderPadLine],
            stockRefToAdd: undefined
        };

    case "SET_CURRENTLY_CONSTRUCTING_ORDER_NO" :

      var result = encryptAndStoreValueToLocalStorage(`order-pad-order-no:${loggedInContactId}Enc`, action.currentlyConstructingOrderNo.toString());
      return {
        ...state,
        //currentlyConstructingOrderNo: action.currentlyConstructingOrderNo,
      };   
      
    case "SET_CURRENTLY_CONSTRUCTING_UNPAYABLE_ORDER_NO" :
      var result = encryptAndStoreValueToLocalStorage(`order-pad-unpayable-order-no:${loggedInContactId}Enc`, action.currentlyConstructingUnpayableOrderNo.toString());
      return {
        ...state,
        currentlyConstructingUnpayableOrderNo: action.currentlyConstructingUnpayableOrderNo,
      };

    case "AMEND_LINE":
      return {
        ...state,
        currentlyAmendingLineId: action.lineToAmendId
      };

    case "UPDATE_LINE_VIA_AMEND_LINE":
        var updatedOrderPadLines = state.lines.map((line) => {
          return line.lineId === state.currentlyAmendingLineId ? action.line : line
        });
  
        var result = encryptAndStoreValueToLocalStorage(`order-pad-lines:${loggedInContactId}Enc`, JSON.stringify(updatedOrderPadLines));
        return {
          ...state,
          //clear out for next batch
          aliasRefToAdd: undefined,
          extendedDescriptionToAdd: undefined,
          lines: updatedOrderPadLines,
          stockRefToAdd: undefined
        };

    case "UPDATE_LINE":
      var updatedOrderPadLines = state.lines.map((line) => {
        return line?.lineId === action?.updatedLine?.lineId ? action.updatedLine : line
      });

      var result = encryptAndStoreValueToLocalStorage(`order-pad-lines:${loggedInContactId}Enc`, JSON.stringify(updatedOrderPadLines));
      return {
        ...state,
        //clear out for next batch
        aliasRefToAdd: undefined,
        extendedDescriptionToAdd: undefined,
        lines: updatedOrderPadLines,
        stockRefToAdd: undefined
      };
      
    case "UPDATE_UNPAYABLE_LINE_VIA_AMEND_LINE":

      var updatedUnpayableOrderPadLines = state.unpayableOrderLines.map((line) => {
        return line?.lineId === action?.updatedUnpayableLine?.lineId ? action.updatedUnpayableLine : line
      });

      var result = encryptAndStoreValueToLocalStorage(`order-pad-unpayable-lines:${loggedInContactId}Enc`, JSON.stringify(updatedUnpayableOrderPadLines));
      return {
        ...state,
        unpayableOrderLines: updatedUnpayableOrderPadLines,
      };

    case "CLONE_LINE":
        return {
          ...state,
          currentlyCloningLineId: action.lineToCloneId
        };

    case "REMOVE_LINE":
      let newLinesState = state.lines.filter(line => { return line.lineId !== action.lineToRemoveId})

      let newUnPayableLinesState : OrderPadLine[] | undefined = undefined;
    
      //only do this if allocation is handled by the API & the order is not put in a holding table and this handled by the desktop app
      if(action.useWebApiForStockPieceAllocation){ 
        //the API will reissue the deallocateAt date when a new line is added to the order-pad, and all lines on the order-pad are handled with this single datetime - all are removed if the user doesn't click to continue shopping - so change the deallocateAt date (against each line AND against each allocatedStockPiece on each line) to that of the newly added piece... that is IF this part of the transaction (serverside) was successful, so if the passed in date is undefined, keep the existing date
        for(let i= 0; i < newLinesState.length; i++){
          newLinesState[i].realitexOrderLineNumber = i+1; //the realitexOrderLineNumber is required for the web api allocation routine, so this is just set to the index of the item in the array (plus 1 as it has to start with 1)

          newLinesState[i].deallocateAt = action.deallocateAt ? action.deallocateAt : newLinesState[i].deallocateAt;
        }

        //if usingWebApiAllocation && user removes an allocated line that is also in the unpayableorderlines, newUnPayableLinesState will hold the new unpayableOrderLines  after the line has also been removed from there ... has to be a roll that is being removed though, if they remove a cut for the same stock ref - this functionality won't be triggered
        const orderLineBeingRemoved = state.lines.filter(orderLine => {return orderLine.lineId === action.lineToRemoveId });
        if(orderLineBeingRemoved && orderLineBeingRemoved.length > 0){
          const unpayableLinesHasThisStockRefInAndIsRoll = state.unpayableOrderLines.some((orderLine) => orderLineBeingRemoved[0].unit?.toUpperCase() === 'ROLL' && orderLine.stockReference === orderLineBeingRemoved[0].stockReference);

          if(unpayableLinesHasThisStockRefInAndIsRoll){
            //pull back into this array all orderlines that are not this stockRef
              newUnPayableLinesState = state.unpayableOrderLines.filter(line => {
              return line.stockReference !== orderLineBeingRemoved[0].stockReference
            })
          }
        }
      }

      var result = encryptAndStoreValueToLocalStorage(`order-pad-lines:${loggedInContactId}Enc`, JSON.stringify(newLinesState));
      if(newUnPayableLinesState)
        var result = encryptAndStoreValueToLocalStorage(`order-pad-unpayable-lines:${loggedInContactId}Enc`, JSON.stringify(newUnPayableLinesState));

      return {
        ...state,
        lines: newLinesState,
        unpayableOrderLines: newUnPayableLinesState ? newUnPayableLinesState: state.unpayableOrderLines

      }

    case "REMOVE_UNPAYABLE_LINE":

      let newUnpayableLinesState = state.unpayableOrderLines.filter(line => {
          return line.lineId !== action.lineToRemoveId
      })

      var result = encryptAndStoreValueToLocalStorage(`order-pad-unpayable-lines:${loggedInContactId}Enc`, JSON.stringify(newUnpayableLinesState));
      return {
        ...state,
        unpayableOrderLines: newUnpayableLinesState,
        orderLineBeingAmendedAsNonPayableRemoved: action.orderLineBeingAmendedAsNonPayableRemoved
      }


    case "REMOVE_UNPAYABLE_LINE_VIA_AMEND":

        let unpayableLinesStateNew = state.unpayableOrderLines.filter(line => {
            return line.lineId !== action.lineToRemoveId
        })
  
        var result = encryptAndStoreValueToLocalStorage(`order-pad-unpayable-lines:${loggedInContactId}Enc`, JSON.stringify(unpayableLinesStateNew));
        return {
          ...state,
          unpayableOrderLines: unpayableLinesStateNew
        }

    case "CLEAR_AMENDED_ORDER_LINE_AS_NONPAYABLE_REMOVED":
      return {
        ...state,
        orderLineBeingAmendedAsNonPayableRemoved: undefined
      }

    case "UPDATE_ADD_ITEM":
      return {
        ...state,
        extendedDescriptionToAdd: action.extendedDescriptionToAdd,
        aliasRefToAdd: action.aliasRefToAdd,
        stockRefToAdd: action.stockRef

    
      }

    case "UPDATE_ADDRESS":
      return {
        ...state,
        address: action.address

        
      }

    case "UPDATE_ADDITIONAL_INFO":
      return {
        ...state,
        comments: action.comments,
        yourReference: action.yourReference

       
      }

    case "UPDATE_REQUIRED_DATE":
      return {
        ...state,
        requiredDate: action.requiredDate,
      }

    case "UPDATE_SUBMIT_STATUS":
      return {
        ...state,
        isSubmitting: action.isSubmitting

      }

    case "UPDATE_SUBMIT_PROCESSING":
      return {
        ...state,
        submitProcessing: {...state.submitProcessing, ...action.data}
      }

    case "RESET_ORDER_PAD":
      localStorage.removeItem(`order-pad-order-no:${loggedInContactId}Enc`);
      localStorage.removeItem(`order-pad-unpayable-order-no:${loggedInContactId}Enc`);
      localStorage.removeItem(`order-pad-lines:${loggedInContactId}Enc`);
      localStorage.removeItem(`order-pad-unpayable-lines:${loggedInContactId}Enc`);
      return {
        ...resetOrderPadState,
        submitProcessing: state.submitProcessing
      } 
    case "RESET_ORDER_PAD_LINES":
      localStorage.removeItem(`order-pad-lines:${loggedInContactId}Enc`);
      localStorage.removeItem(`order-pad-unpayable-lines:${loggedInContactId}Enc`);
      return {
        ...resetOrderPadState,
        submitProcessing: state.submitProcessing
      }
    case "RESET_SUBMITPROCESSING":
      return {...state, submitProcessing: undefined}

    case "UPDATE_PRICE_LOADING":
      return { ...state, priceLoading: action.priceLoading}

    case "UPDATE_STOCK_AVAILBILITY_LOADING":
      return {...state,         stockAvailableLoading: action.stockAvailableLoading}

    case "IMPORT_LINES":
      return {...state, importLines: action.active}

    case "UPDATE_DEALLOCATE_AT": 

      if(action.useWebApiForStockPieceAllocation){
        const orderPadLines = [...state.lines];

        const orderPadLinesWithUpdatedDeallocateAtDate = orderPadLines.map(orderPadLine => ({...orderPadLine, deallocateAt: action.newDeallocateAtDateTime}));
        var result = encryptAndStoreValueToLocalStorage(`order-pad-lines:${loggedInContactId}Enc`, JSON.stringify(orderPadLinesWithUpdatedDeallocateAtDate));

        return{
          ...state,
          lines: orderPadLinesWithUpdatedDeallocateAtDate
        }
      }
  }
  return state;
};
